import React, { useState } from "react";
import { Colors } from "../../Constants/colors";
import { DropdownOptions } from "../dropdownOptions";
import { Separator } from "../Separator";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";

export interface MultiOptions {
  optionsTitle: string;
  category: string;
  options: DropdownOptions[];
}

interface Props {
  dropdownOptions: MultiOptions[];
  showDropdown: boolean;
  onOptionsSelected: (selectedData: DropdownOptions) => unknown;
  selectedOptions: DropdownOptions[] | null;
  setOptions: (options: DropdownOptions[]) => unknown;
}

// Custom Checkbox Component with modern styling
const ModernCheckbox = ({
  isChecked,
  onChange,
}: {
  isChecked: boolean;
  onChange: (e: React.MouseEvent) => void;
}) => {
  return (
    <>
      <style>
        {`
          @keyframes checkboxCheck {
            0% {
              transform: scale(0);
            }
            50% {
              transform: scale(1.2);
            }
            100% {
              transform: scale(1);
            }
          }

          .custom-checkbox {
            position: relative;
            width: 20px;
            height: 20px;
            border: 2px solid ${Colors.LightGrey};
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${Colors.White};
          }

          .custom-checkbox:hover {
            border-color: ${Colors.TangerineOrange};
            box-shadow: 0 0 0 3px rgba(254, 168, 1, 0.1);
          }

          .custom-checkbox.checked {
            background-color: ${Colors.TangerineOrange};
            border-color: ${Colors.TangerineOrange};
          }

          .custom-checkbox.checked .checkmark {
            animation: checkboxCheck 0.3s ease-out;
          }

          .checkmark {
            color: ${Colors.White};
            font-size: 12px;
            font-weight: bold;
          }
        `}
      </style>
      <div
        className={`custom-checkbox ${isChecked ? "checked" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onChange(e);
        }}
      >
        {isChecked && (
          <svg
            className="checkmark"
            width="12"
            height="10"
            viewBox="0 0 12 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 5L4.5 8.5L11 1.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </>
  );
};

// Modern Action Button Component
const ModernActionButton = ({
  hasOptions,
  onSelectAll,
  onClearAll,
}: {
  hasOptions: boolean;
  onSelectAll: () => void;
  onClearAll: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        hasOptions ? onClearAll() : onSelectAll();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: "6px 12px",
        fontSize: "12px",
        fontFamily: FontFamily.POPPINS_REGULAR,
        fontWeight: "500",
        color: !hasOptions
          ? isHovered
            ? Colors.TangerineOrange
            : Colors.Grey
          : isHovered
          ? Colors.Tomato
          : Colors.AshRose,
        backgroundColor: isHovered
          ? !hasOptions
            ? Colors.LIGHT_TANGERINE_ORANGE
            : "rgba(255, 0, 0, 0.08)"
          : "transparent",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      {!hasOptions ? (
        <>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 7H12M7 2V12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Select all
        </>
      ) : (
        <>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 3L3 11M3 3L11 11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Clear all
        </>
      )}
    </button>
  );
};

export const MultiDropdownOptions = ({
  dropdownOptions,
  showDropdown,
  onOptionsSelected,
  selectedOptions,
  setOptions,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredOption, setHoveredOption] = useState<string | number | null>(
    null,
  );

  if (!showDropdown) return <></>;

  const handleSelectAll = ({ category }: { category: string }) => {
    const allOptionsOfCategory = dropdownOptions.find(
      ({ category: dropdownCategory }) => dropdownCategory === category,
    );
    if (allOptionsOfCategory) {
      const newOptions = allOptionsOfCategory.options;
      // Remove duplicates before adding
      const existingValues = new Set(
        (selectedOptions || []).map((opt) => opt.value),
      );
      const uniqueNewOptions = newOptions.filter(
        (opt) => !existingValues.has(opt.value),
      );
      setOptions([...(selectedOptions || []), ...uniqueNewOptions]);
    }
  };

  const handleClearAll = ({ category }: { category: string }) => {
    const categoriesItem =
      (selectedOptions || []).filter(
        ({ category: dropdownCategory }) =>
          (dropdownCategory || "") !== category,
      ) || [];
    setOptions([...(categoriesItem || [])]);
  };

  const buildActionItem = ({ category }: { category: string }) => {
    const categoriesArr: { [key: string]: any } = {};
    (selectedOptions || []).forEach(({ value: selectedValue }) => {
      (dropdownOptions || []).forEach(({ category, options }) => {
        const foundIndex = options.findIndex(
          ({ value: dropDownValue }) => selectedValue === dropDownValue,
        );
        if (foundIndex >= 0) {
          if (!categoriesArr[category]) {
            categoriesArr[category] = [];
          }
          categoriesArr[category].push(selectedValue);
        }
      });
    });

    const hasOptionsFromCategory = (categoriesArr[category] || []).length > 0;
    
    return (
      <ModernActionButton
        hasOptions={hasOptionsFromCategory}
        onSelectAll={() => handleSelectAll({ category })}
        onClearAll={() => handleClearAll({ category })}
      />
    );
  };

  // Filter options based on search query
  const filteredDropdownOptions = dropdownOptions
    .map((section) => ({
      ...section,
      options: section.options.filter((option) =>
        option.text.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    }))
    .filter((section) => section.options.length > 0);

  return (
    <>
      <style>
        {`
          @keyframes dropdownSlideIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .dropdown-container {
            animation: dropdownSlideIn 0.2s ease-out;
          }

          .option-row {
            transition: all 0.15s ease;
          }

          .option-row:hover {
            background-color: ${Colors.VeryLightGrey};
            border-radius: 6px;
          }

          .search-input {
            outline: none;
            transition: all 0.2s ease;
          }

          .search-input:focus {
            border-color: ${Colors.TangerineOrange};
            box-shadow: 0 0 0 3px rgba(254, 168, 1, 0.1);
          }

          .dropdown-scrollbar::-webkit-scrollbar {
            width: 8px;
          }

          .dropdown-scrollbar::-webkit-scrollbar-track {
            background: ${Colors.VeryLightGrey};
            border-radius: 10px;
          }

          .dropdown-scrollbar::-webkit-scrollbar-thumb {
            background: ${Colors.LightGrey};
            border-radius: 10px;
          }

          .dropdown-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${Colors.Grey};
          }
        `}
      </style>
      <div
        className="dropdown-container flex flex-col rounded-lg"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: 0,
          right: 0,
          zIndex: 999,
          width: "100%",
          border: `1px solid ${Colors.LightAshRose}`,
          backgroundColor: Colors.White,
          boxShadow:
            "0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)",
          cursor: "default",
          maxHeight: "400px",
          overflow: "hidden",
        }}
      >
        {/* Search Input */}
        <div style={{ padding: "16px", paddingBottom: "12px" }}>
          <div style={{ position: "relative" }}>
            <svg
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z"
                stroke={Colors.Grey}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L10.5 10.5"
                stroke={Colors.Grey}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search options..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                padding: "10px 12px 10px 38px",
                border: `2px solid ${Colors.LightGrey}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: FontFamily.POPPINS_REGULAR,
                color: Colors.DarkGrey,
                backgroundColor: Colors.VeryLightGrey,
              }}
            />
          </div>
        </div>

        {/* Options List */}
        <div
          className="dropdown-scrollbar"
          style={{
            overflowY: "auto",
            padding: "0 16px 16px 16px",
          }}
        >
          {filteredDropdownOptions.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "24px",
                color: Colors.Midgrey,
                fontFamily: FontFamily.POPPINS_REGULAR,
                fontSize: "14px",
              }}
            >
              No options found
            </div>
          ) : (
            filteredDropdownOptions.map(
              ({ optionsTitle, category, options }, index) => (
                <React.Fragment key={index}>
                  <div
                    className="flex flex-row items-center justify-between"
                    style={{
                      marginBottom: "12px",
                      marginTop: index > 0 ? "16px" : "0",
                    }}
                  >
                    <div
                      className="text-sm font-semibold"
                      style={{
                        color: Colors.DarkGrey,
                        fontFamily: FontFamily.POPPINS_REGULAR,
                      }}
                    >
                      {optionsTitle}
                    </div>
                    <div>{buildActionItem({ category })}</div>
                  </div>

                  <Separator />

                  <div
                    style={{
                      marginTop: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {options.map((option, optIndex) => (
                      <div
                        key={optIndex}
                        className="option-row flex flex-row gap-3 content-center items-center"
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          backgroundColor:
                            hoveredOption === option.value
                              ? Colors.VeryLightGrey
                              : "transparent",
                        }}
                        onMouseEnter={() => setHoveredOption(option.value)}
                        onMouseLeave={() => setHoveredOption(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOptionsSelected(option);
                        }}
                      >
                        <ModernCheckbox
                          isChecked={
                            !!selectedOptions?.find(
                              ({ value }) => value === option.value,
                            )
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            onOptionsSelected(option);
                          }}
                        />
                        <div
                          className="text-sm"
                          style={{
                            color: Colors.DarkGrey,
                            fontFamily: FontFamily.POPPINS_REGULAR,
                          }}
                        >
                          {option.text}
                        </div>
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              ),
            )
          )}
        </div>
      </div>
    </>
  );
};

export default MultiDropdownOptions;
